import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Calendar,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { auditLogAPI, AuditLog, AuditLogFilters } from '../../services/api';
import { useApiAuth } from '../../hooks/useApiAuth';
import { SkeletonAuditLogs } from '../Skeleton/SkeletonLoader';
import { Pagination as PaginationComponent } from '../Pagination';

const AuditLogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid;

  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;

    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const FiltersSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const LogsTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 16px;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
  color: #374151;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 80px;
    gap: 12px;
    padding: 12px 16px;
  }
`;

const TableRow = styled.div<{ expanded?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-items: center;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 80px;
    gap: 12px;
    padding: 12px 16px;
  }
`;

const TableCell = styled.div`
  font-size: 14px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span<{ status: 'success' | 'error' | 'warning' | 'info' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.status) {
      case 'success':
        return 'background: #d1fae5; color: #065f46;';
      case 'error':
        return 'background: #fee2e2; color: #991b1b;';
      case 'warning':
        return 'background: #fef3c7; color: #92400e;';
      case 'info':
        return 'background: #dbeafe; color: #1e40af;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ExpandedRow = styled.div`
  background: #f9fafb;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const ExpandedContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ExpandedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ExpandedLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExpandedValue = styled.div`
  font-size: 14px;
  color: #111827;
  word-break: break-word;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &[aria-current='page'] {
    background: #3b82f6;
    color: white;
  }

  &:hover {
    background: #f9fafb;
  }

  &[aria-current='page']:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #6b7280;
`;

// Helper function to map backend status to UI status
const mapStatusToUI = (status: string): 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
      return 'info';
    default:
      return 'info';
  }
};

// Helper function to format action for display
const formatAction = (action: string): string => {
  switch (action) {
    case 'CREATE':
      return 'Assessment Created';
    case 'READ':
      return 'Assessment Viewed';
    case 'UPDATE':
      return 'Assessment Updated';
    case 'DELETE':
      return 'Assessment Deleted';
    case 'LOGIN':
      return 'User Login';
    case 'LOGOUT':
      return 'User Logout';
    case 'LOGIN_FAILED':
      return 'Login Failed';
    case 'DATA_EXPORT':
      return 'Data Export';
    case 'DATA_IMPORT':
      return 'Data Import';
    case 'SYSTEM_ERROR':
      return 'System Error';
    default:
      return action;
  }
};

// Helper function to format resource for display
const formatResource = (resource: string): string => {
  switch (resource) {
    case 'ASSESSMENT':
      return 'LACE Tool';
    case 'USER':
      return 'User Management';
    case 'AUTHENTICATION':
      return 'Authentication';
    case 'REPORTS':
      return 'Reports';
    case 'SYSTEM':
      return 'System';
    case 'AUDIT_LOG':
      return 'Audit Logs';
    default:
      return resource;
  }
};

const AuditLogs: React.FC = () => {
  const { getAccessToken } = useApiAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    user: '',
    action: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Fetch audit logs from API
  const fetchAuditLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: AuditLogFilters = {
        page: currentPage,
        limit: itemsPerPage,
        search: filters.search || undefined,
        userName: filters.user || undefined,
        action: filters.action || undefined,
        status: filters.status || undefined,
        startDate: filters.dateFrom || undefined,
        endDate: filters.dateTo || undefined,
        sortBy: 'timestamp',
        sortOrder: 'desc'
      };

      // Use authenticated API service
      const result = await auditLogAPI.getAuditLogs(params);
      setLogs(result.logs || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalItems(result.total || 0);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      // Set empty logs on error
      setLogs([]);
      setTotalPages(1);
      setTotalItems(0);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts or filters change
  useEffect(() => {
    fetchAuditLogs();
  }, [currentPage, filters, itemsPerPage]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={16} />;
      case 'error':
        return <XCircle size={16} />;
      case 'warning':
        return <AlertTriangle size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  // Export audit logs
  const handleExport = async () => {
    try {
      const params = {
        startDate: filters.dateFrom || undefined,
        endDate: filters.dateTo || undefined,
        format: 'csv'
      };
      
      const blob = await auditLogAPI.exportAuditLogs(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      setError('Failed to export audit logs. Please try again.');
    }
  };

  return (
    <AuditLogsContainer>
      <HeaderSection>
        <PageTitle>Audit Logs</PageTitle>
        <ActionButtons>
          <Button variant="secondary" onClick={handleExport}>
            <Download size={16} />
            Export
          </Button>
          <Button variant="primary">
            <Filter size={16} />
            Advanced Filters
          </Button>
        </ActionButtons>
      </HeaderSection>

      <FiltersSection>
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Search</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Search logs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>User</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Filter by user name..."
              value={filters.user}
              onChange={(e) => handleFilterChange('user', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Action</FilterLabel>
            <FilterSelect
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Assessment Created</option>
              <option value="READ">Assessment Viewed</option>
              <option value="UPDATE">Assessment Updated</option>
              <option value="DELETE">Assessment Deleted</option>
              <option value="LOGIN">User Login</option>
              <option value="LOGOUT">User Logout</option>
              <option value="LOGIN_FAILED">Login Failed</option>
              <option value="DATA_EXPORT">Data Export</option>
              <option value="DATA_IMPORT">Data Import</option>
              <option value="SYSTEM_ERROR">System Error</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
              <option value="INFO">Info</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date From</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date To</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>
      </FiltersSection>

      <LogsTable>
        <TableHeader>
          <div>Timestamp</div>
          <div>User</div>
          <div>Action</div>
          <div>Resource</div>
          <div>Status</div>
          <div></div>
        </TableHeader>

        {error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
            {error}
          </div>
        )}
        
        {loading ? (
          <SkeletonAuditLogs />
        ) : logs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            No audit logs found matching your criteria.
          </div>
        ) : (
          logs.map((log) => (
            <React.Fragment key={log.id}>
              <TableRow onClick={() => toggleRowExpansion(log.id)}>
                <TableCell>
                  <Calendar size={16} />
                  {format(parseISO(log.timestamp), 'MMM dd, yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  <User size={16} />
                  {log.userName}
                </TableCell>
                <TableCell>{formatAction(log.action)}</TableCell>
                <TableCell>{formatResource(log.resource)}</TableCell>
                <TableCell>
                  <StatusBadge status={mapStatusToUI(log.status)}>
                    {getStatusIcon(mapStatusToUI(log.status))}
                    {log.status.toLowerCase()}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <ExpandButton>
                    {expandedRows.has(log.id) ? 
                      <ChevronUp size={16} /> : 
                      <ChevronDown size={16} />
                    }
                  </ExpandButton>
                </TableCell>
              </TableRow>

              {expandedRows.has(log.id) && (
                <ExpandedRow>
                  <ExpandedContent>
                    <ExpandedSection>
                      <ExpandedLabel>Patient Information</ExpandedLabel>
                      <ExpandedValue>
                        {log.details.patientName || 'N/A'}
                      </ExpandedValue>
                      <ExpandedLabel>Facility</ExpandedLabel>
                      <ExpandedValue>
                        {log.details.facilityName || 'N/A'}
                      </ExpandedValue>
                      <ExpandedLabel>LACE Score</ExpandedLabel>
                      <ExpandedValue>
                        {log.details.score || 'N/A'}
                      </ExpandedValue>
                    </ExpandedSection>

                    <ExpandedSection>
                      <ExpandedLabel>Technical Details</ExpandedLabel>
                      <ExpandedValue>
                        IP: {log.details.ipAddress || 'N/A'}
                      </ExpandedValue>
                      <ExpandedLabel>Changes</ExpandedLabel>
                      <ExpandedValue>
                        {log.details.changes ? 
                          JSON.stringify(log.details.changes, null, 2) : 
                          'No changes recorded'
                        }
                      </ExpandedValue>
                    </ExpandedSection>
                  </ExpandedContent>
                </ExpandedRow>
              )}
            </React.Fragment>
          ))
        )}

        <PaginationComponent
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showPageSizeSelector={true}
          pageSizeOptions={[5, 10, 25, 50]}
          showInfo={true}
        />
      </LogsTable>
    </AuditLogsContainer>
  );
};

export default AuditLogs;
